import React, { useEffect, useState, useCallback } from "react";
import { FINANCE_V2_HOSTNAME } from "../../../../common-utilities/utils/end-points";
import { HTTP_METHODS, invokeApi } from "../../../utils/http-service";
import { FINANCE_REST_URLS } from "../../../../common-utilities/utils/end-points/finance-v2";
import { Button, TextField, IconButton } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import CustomModal from "../../../../common-utilities/core/modal";
import { toast } from "react-toastify";
import "./index.scss";

const FAItemsMaster = ({ open, onClose }) => {
  const [selectedFAType, setSelectedFAType] = useState(null);
  const [faItemsList, setFAItemsList] = useState([]);
  const [newFAItemName, setNewFAItemName] = useState("");
  const [faMasterData, setFAMasterData] = useState({
    depreciationBookCode: [],
    depreciationMethod: [],
    faClassCode: [],
    faSubClassCode: [],
    noOfDepreciationYear: [],
  });
  const [isLoading, setIsLoading] = useState(false);

  const loadFAItems = useCallback(async () => {
    if (!open) return;

    setIsLoading(true);
    try {
      const response = await invokeApi(
        HTTP_METHODS.GET,
        `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.FA_ITEMS_MASTER}/1`
      );

      if (response) {
        setFAMasterData({
          depreciationBookCode: response.depreciationBookCode || [],
          depreciationMethod: response.depreciationMethod || [],
          faClassCode: response.faClassCode || [],
          faSubClassCode: response.faSubClassCode || [],
          noOfDepreciationYear: response.noOfDepreciationYear || [],
        });

        if (selectedFAType) {
          const items = response[selectedFAType] || [];
          setFAItemsList(
            items.map((item, index) => ({
              id: index + 1,
              label: item,
              value: item,
            }))
          );
        }
      }
    } catch (err) {
      console.error("Error loading FA items:", err);
      toast.error("Failed to load FA items");
    } finally {
      setIsLoading(false);
    }
  }, [open, selectedFAType]);

  useEffect(() => {
    loadFAItems();
  }, [loadFAItems]);

  const updateFAItems = useCallback(
    async (updatedItems) => {
      if (!selectedFAType) return;

      setIsLoading(true);
      try {
        const payload = {
          ...faMasterData,
          [selectedFAType]: updatedItems.map((item) => item.label),
        };

        const response = await invokeApi(
          HTTP_METHODS.PUT,
          `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.FA_ITEMS_MASTER}/1`,
          payload
        );

        if (!response.message) {
          toast.success("FA items updated successfully");
          loadFAItems();
        } else {
          toast.error(response.message);
          loadFAItems();
        }
      } catch (err) {
        console.error("Error updating FA items:", err);
        toast.error("Failed to update FA items");
      } finally {
        setIsLoading(false);
      }
    },
    [selectedFAType, faMasterData, loadFAItems]
  );

  const handleFATypeSelect = useCallback((type) => {
    setSelectedFAType(type);
    setFAItemsList([]);
  }, []);

  const handleAddItem = useCallback(() => {
    if (!newFAItemName.trim() || !selectedFAType) return;

    const newItem = {
      id: faItemsList.length + 1,
      label: newFAItemName.trim(),
      value: newFAItemName.trim(),
    };

    const updatedItems = [...faItemsList, newItem];
    setFAItemsList(updatedItems);
    setNewFAItemName("");
    updateFAItems(updatedItems);
  }, [newFAItemName, faItemsList, selectedFAType, updateFAItems]);

  const handleEditItem = useCallback((itemId) => {
    setFAItemsList((prevItems) =>
      prevItems.map((item) =>
        item.id === itemId
          ? { ...item, isEditing: true, tempLabel: item.label }
          : item
      )
    );
  }, []);

  const handleSaveEdit = useCallback(
    (item) => {
      const updatedItems = faItemsList.map((i) =>
        i.id === item.id
          ? {
              ...i,
              label: item.tempLabel,
              value: item.tempLabel,
              isEditing: false,
            }
          : i
      );
      setFAItemsList(updatedItems);
      updateFAItems(updatedItems);
    },
    [faItemsList, updateFAItems]
  );

  const handleCancelEdit = useCallback((itemId) => {
    setFAItemsList((prevItems) =>
      prevItems.map((item) =>
        item.id === itemId ? { ...item, isEditing: false } : item
      )
    );
  }, []);

  const handleDeleteItem = useCallback(
    (itemId) => {
      const updatedItems = faItemsList.filter((item) => item.id !== itemId);
      setFAItemsList(updatedItems);
      updateFAItems(updatedItems);
    },
    [faItemsList, updateFAItems]
  );

  const handleClose = useCallback(() => {
    setSelectedFAType(null);
    setFAItemsList([]);
    setNewFAItemName("");
    onClose();
  }, [onClose]);
  return (
    <CustomModal
      title="FA Items Master"
      disabledOutsideClick={true}
      open={open}
      onClose={handleClose}
    >
      <div className="finance-v2-fa-items-master">
        {!selectedFAType ? (
          <div className="fa-type-selection">
            <h3>Select FA Type</h3>
            <div className="fa-type-buttons">
              <Button
                variant="contained"
                onClick={() => handleFATypeSelect("faClassCode")}
                disabled={isLoading}
              >
                FA Class Code
              </Button>
              <Button
                variant="contained"
                onClick={() => handleFATypeSelect("faSubClassCode")}
                disabled={isLoading}
              >
                FA Subclass Code
              </Button>
              <Button
                variant="contained"
                onClick={() => handleFATypeSelect("depreciationBookCode")}
                disabled={isLoading}
              >
                Depreciation Book Code
              </Button>
              <Button
                variant="contained"
                onClick={() => handleFATypeSelect("depreciationMethod")}
                disabled={isLoading}
              >
                Depreciation Method
              </Button>
              <Button
                variant="contained"
                onClick={() => handleFATypeSelect("noOfDepreciationYear")}
                disabled={isLoading}
              >
                No. Of Depreciation Year
              </Button>
              <Button
                variant="contained"
                onClick={() => handleFATypeSelect("faPostingGroup")}
                disabled={isLoading}
              >
                FA Posting
              </Button>
            </div>
          </div>
        ) : (
          <div className="fa-items-management">
            <div className="fa-items-header">
              <div className="header-title">
                <IconButton
                  onClick={() => handleFATypeSelect(null)}
                  size="small"
                  sx={{ color: "primary.main" }}
                  disabled={isLoading}
                >
                  <ArrowBackIcon />
                </IconButton>
                <h3>{selectedFAType.replace(/([A-Z])/g, " $1").trim()}</h3>
              </div>
            </div>
            <div className="new-fa-item">
              <TextField
                label="New Item Name"
                value={newFAItemName}
                onChange={(e) => setNewFAItemName(e.target.value)}
                size="small"
                fullWidth
                type={selectedFAType === "noOfDepreciationYear" && "number"}
                disabled={isLoading}
              />
              <Button
                variant="contained"
                onClick={handleAddItem}
                startIcon={<SaveIcon />}
                disabled={isLoading || !newFAItemName.trim()}
              >
                Add
              </Button>
            </div>
            <div className="fa-items-list">
              {faItemsList.length > 0 ? (
                faItemsList.map((item) => (
                  <div key={item.id} className="fa-item">
                    {item.isEditing ? (
                      <div className="edit-item">
                        <TextField
                          value={item.tempLabel}
                          onChange={(e) => {
                            setFAItemsList((prevItems) =>
                              prevItems.map((i) =>
                                i.id === item.id
                                  ? { ...i, tempLabel: e.target.value }
                                  : i
                              )
                            );
                          }}
                          size="small"
                          fullWidth
                          disabled={isLoading}
                        />
                        <IconButton
                          onClick={() => handleSaveEdit(item)}
                          color="primary"
                          size="small"
                          sx={{ color: "primary.main" }}
                          disabled={isLoading}
                        >
                          <SaveIcon />
                        </IconButton>
                        <IconButton
                          onClick={() => handleCancelEdit(item.id)}
                          color="error"
                          size="small"
                          sx={{ color: "error.main" }}
                          disabled={isLoading}
                        >
                          <CancelIcon />
                        </IconButton>
                      </div>
                    ) : (
                      <>
                        <span>{item.label}</span>
                        <div className="fa-item-actions">
                          <IconButton
                            onClick={() => handleEditItem(item.id)}
                            size="small"
                            sx={{ color: "primary.main" }}
                            disabled={isLoading}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            onClick={() => handleDeleteItem(item.id)}
                            color="error"
                            size="small"
                            sx={{ color: "error.main" }}
                            disabled={isLoading}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </div>
                      </>
                    )}
                  </div>
                ))
              ) : (
                <div className="no-items">No items found</div>
              )}
            </div>
          </div>
        )}
      </div>
    </CustomModal>
  );
};

export default FAItemsMaster;
